import { useCallback, useState, type ReactElement, type ReactNode } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  Image,
  pickImageFile,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
  useIsMobile,
  type Omittable,
} from "@salestrak/ui";

import { BANNER_PRESETS } from "#app/lib/banner-presets";
import type { Form } from "#app/lib/forms";
import { useMediaObjectUrl } from "#app/lib/hooks/use-note-media";

type BannerSource = Pick<Form, "bannerUrl" | "bannerMediaId">;

export function hasFormBanner(form: BannerSource): boolean {
  return !!form.bannerUrl || !!form.bannerMediaId;
}

export function useFormBannerUrl(form: BannerSource, variant: "full" | "thumb" = "full"): string | null {
  const uploaded = useMediaObjectUrl(form.bannerUrl ? "" : (form.bannerMediaId ?? ""), variant);
  return form.bannerUrl ?? uploaded;
}

const FORM_BANNER_HEIGHT = 160;
const FORM_BANNER_PHONE_HEIGHT = 96;
export const FORM_BANNER_TILE_HEIGHT = 96;

/** The host clips the band's corners, so the band itself is square. */
export function FormBanner({
  form,
  alt,
  height,
  children,
}: Omittable<{
  form: BannerSource;
  alt: string;
  height?: number;
  children?: ReactNode;
}>) {
  const isMobile = useIsMobile();
  const src = useFormBannerUrl(form);
  return (
    <Box
      background="muted"
      height={height ?? (isMobile ? FORM_BANNER_PHONE_HEIGHT : FORM_BANNER_HEIGHT)}
      overflow="hidden"
      position="relative"
      shrink={false}
    >
      {src ? <Image alt={alt} fill fit="cover" src={src} /> : null}
      {children}
    </Box>
  );
}

/** Recorded width: 4×112 + 3×8 gap + 2×16 padding (popover rungs are 300/360). */
const PICKER_WIDTH = 504;
const SWATCH_HEIGHT = 64;

/** On a phone a bottom sheet: a popover off a door near the top runs off its bottom. */
export function FormBannerPicker({
  bannerUrl,
  onSelect,
  onUpload,
  onRemove,
  trigger,
}: Omittable<{
  bannerUrl?: string | null;
  onSelect: (url: string) => void;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => void;
  trigger: ReactElement;
}>) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const selectBanner = useCallback(
    (url: string) => {
      onSelect(url);
      setOpen(false);
    },
    [onSelect],
  );

  const uploadBanner = useCallback(async () => {
    const file = await pickImageFile();
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
      setOpen(false);
    } finally {
      setUploading(false);
    }
  }, [onUpload]);

  const actions = onRemove ? (
    <Button
      color="destructive"
      onClick={() => {
        onRemove();
        setOpen(false);
      }}
      size={isMobile ? "sm" : "xs"}
      variant="ghost"
    >
      Remove
    </Button>
  ) : null;
  const panels = (
    <>
      <TabsPanel value="gallery">
        <BannerGallery bannerUrl={bannerUrl} onPick={selectBanner} />
      </TabsPanel>
      <TabsPanel value="upload">
        <BannerUpload onUpload={() => void uploadBanner()} uploading={uploading} />
      </TabsPanel>
    </>
  );

  if (isMobile) {
    return (
      <Drawer onOpenChange={setOpen} open={open}>
        <DrawerTrigger render={trigger} />
        <DrawerPopup showBar>
          <DrawerHeader>
            <Box align="center" direction="row" gap="sm" justify="between">
              <DrawerTitle>Banner</DrawerTitle>
              {actions}
            </Box>
          </DrawerHeader>
          <DrawerPanel>
            <Tabs defaultValue="gallery">
              <Box gap="md">
                <TabsList fullWidth>
                  <TabsTab value="gallery">Gallery</TabsTab>
                  <TabsTab value="upload">Upload</TabsTab>
                </TabsList>
                {panels}
              </Box>
            </Tabs>
          </DrawerPanel>
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger render={trigger} />
      <PopoverPopup align="start" padding="none">
        <Box maxWidth="100%" width={PICKER_WIDTH}>
          <Tabs defaultValue="gallery">
            <Box>
              <Box
                align="center"
                direction="row"
                justify="between"
                paddingX="md"
              >
                <TabsList variant="underline">
                  <TabsTab value="gallery">Gallery</TabsTab>
                  <TabsTab value="upload">Upload</TabsTab>
                </TabsList>
                {actions}
              </Box>
              <Separator />
            </Box>
            <Box padding="md">{panels}</Box>
          </Tabs>
        </Box>
      </PopoverPopup>
    </Popover>
  );
}

function BannerGallery({
  bannerUrl,
  onPick,
}: Omittable<{
  bannerUrl?: string | null;
  onPick: (url: string) => void;
}>) {
  return (
    <Box gap="sm">
      <Text size="xs" variant="muted">
        Color & gradient
      </Text>
      <Box columns={{ base: 3, sm: 4 }} gap="sm">
        {BANNER_PRESETS.map((preset) => {
          const selected = bannerUrl === preset.url;
          return (
            <Box<"button">
              key={preset.id}
              aria-label={`Use ${preset.label} banner`}
              aria-pressed={selected}
              as="button"
              border
              borderColor={selected ? "primary" : undefined}
              cursor="pointer"
              fill="width"
              height={SWATCH_HEIGHT}
              onClick={() => onPick(preset.url)}
              overflow="hidden"
              position="relative"
              rounded="md"
              type="button"
            >
              <Image alt="" fill fit="cover" src={preset.url} />
              {selected ? (
                <Box
                  align="center"
                  background="background"
                  height={20}
                  justify="center"
                  position="absolute"
                  right={6}
                  rounded="full"
                  top={6}
                  width={20}
                >
                  <Icon color="primary" name="check" size="xs" />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function BannerUpload({
  uploading,
  onUpload,
}: {
  uploading: boolean;
  onUpload: () => void;
}) {
  const isMobile = useIsMobile();
  return (
    <Box align="center" gap="md" paddingY="lg">
      <Box
        align="center"
        background="muted"
        height={44}
        justify="center"
        rounded="full"
        width={44}
      >
        <Icon color="muted" name="arrow-up" size="lg" />
      </Box>
      <Text weight="semibold">Upload a banner image</Text>
      <Text size="xs" variant="muted">
        Choose a PNG, JPG, GIF, or WebP image.
      </Text>
      <Button
        loading={uploading}
        onClick={onUpload}
        size={isMobile ? "default" : "sm"}
        variant="outline"
      >
        Choose image
      </Button>
    </Box>
  );
}
